import { EuiSelect } from "@inscopix/ideas-eui";
import { captureException } from "@sentry/react";
import { useToolEditorContext } from "components/ToolEditor/Provider/ToolEditorProvider";
import { formatToolResources } from "components/ToolParamsGrid/GridEditorResourceTierChoice";
import { useMemo } from "react";
import { ToolSpecV3 } from "types/json-schemas/ToolSpecV3";
import {
  RESOURCE_TIERS,
  RESOURCE_TIERS_BY_ID,
  ResourceTier,
} from "types/ResourceTiers";
import { isDefined } from "utils/isDefined";

export interface ResourcesEditorProps {
  resources: ToolSpecV3["resources"];
}
export const ResourcesEditor = ({ resources }: ResourcesEditorProps) => {
  const updateResourceTier = useToolEditorContext((s) => s.updateResourceTier);
  const selectedTierId = useMemo(() => {
    const tier = Object.values(RESOURCE_TIERS).find(
      (tier) =>
        tier.cpu === resources.cpu &&
        tier.gpu === resources.gpu &&
        tier.memory === resources.memory,
    );
    if (tier === undefined) {
      /**
       * Note: Since tiers are not stored as an enum on the spec but we want to display only valid resource tiers for custom tools
       * the backend sets tier 1 as the default so we should always have a match. This is here in case someone breaks that in the future.
       */
      captureException("Tier set on draft spec does not match resource tiers");
    }
    return tier?.id ?? RESOURCE_TIERS_BY_ID[1].id;
  }, [resources.cpu, resources.gpu, resources.memory]);

  return (
    <EuiSelect
      value={String(selectedTierId)}
      options={Object.values(RESOURCE_TIERS_BY_ID)
        .filter(isDefined)
        .map((tier) => ({
          value: String(tier.id),
          text: formatToolResources(
            {
              resources: RESOURCE_TIERS_BY_ID[selectedTierId],
              credits: RESOURCE_TIERS_BY_ID[selectedTierId].credits,
            },
            tier.id,
          ),
          selected: String(tier.id) === String(selectedTierId),
        }))}
      onChange={(e) => {
        updateResourceTier(Number(e.target.value) as ResourceTier["id"]);
      }}
    />
  );
};
